import { Grid, Card, Text, Stack, Box, Table, Flex, Badge, Group, ThemeIcon } from '@mantine/core';
import InfoCard from '../InfoCard';
import EvidencesDistributionCard from './EvidencesDistributionCard';
import { IconCalendar, IconChartPie, IconFileCheck, IconFileText } from '@tabler/icons-react';
import { useSelector } from 'react-redux';
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es"; 

const getAllKPIs = (pei) =>
  pei?.Objetivos?.flatMap(objetivo =>
    objetivo?.criterios?.flatMap(criterio =>
      criterio?.kpiList || []
    )
  ) || [];

const DashboardEvidences = () => {
  const { data: evidences, loading: loadingEvidences } = useSelector((state) => state.evidence);
  const { data: pei, loading: loadingPei } = useSelector((state) => state.pei.matrixPei);

  const loading = (evidences.length === 0 && (loadingEvidences || loadingPei));

  const totalEvidences = evidences.length;
  const totalValoradas = evidences.filter((ev) => ev.tieneValoracion).length;
  
  const allKpis = getAllKPIs(pei);
  const totalKpis = allKpis.length;
  const coveredKpis = new Set(evidences.flatMap((ev) => ev.kpis.map((k) => k.Id))).size;

  dayjs.extend(relativeTime);
  dayjs.locale("es");

  const ultimaActualizacionTimestamp = evidences.length ? Math.max(...evidences.map(ev => new Date(ev.dt_lu).getTime())) : null;
  const ultimaActualizacion = ultimaActualizacionTimestamp
    ? {
        label: dayjs(ultimaActualizacionTimestamp).fromNow(),
        date: dayjs(ultimaActualizacionTimestamp).format("DD/MM/YYYY")
      }
    : null;

  const twoWeeksAgo = dayjs().subtract(14, 'day');
  const recentEvidences = evidences
    .filter((ev) => dayjs(ev.dt_lu).isAfter(twoWeeksAgo))
    .sort((a, b) => new Date(b.dt_lu) - new Date(a.dt_lu));

  const evidenciasPorKpi = evidences.reduce((acc, ev) => {
    ev.kpis.forEach(kpi => {
      acc[kpi.Id] = (acc[kpi.Id] || 0) + 1;
    });
    return acc;
  }, {});

  return (
    <Stack>
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
          <InfoCard
            title="Total de Evidencias"
            value={totalEvidences}
            subtitle={`+${recentEvidences.length} en los ultimos 14 dias`} 
            size="sm"
            icon={<IconFileText size={22} stroke={1.5}/>}
            loading={loading}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
          <InfoCard
            title="Evidencias Valoradas"
            value={totalValoradas}
            subtitle={`${Math.round((totalValoradas / totalEvidences) * 100)}% del total`}
            size="sm"
            icon={<IconFileCheck size={22} stroke={1.5}/>}
            loading={loading}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
          <InfoCard
            title="KPIs cubiertos"
            value={coveredKpis}
            subtitle={`De un total de ${totalKpis} KPIs`}
            size="sm"
            icon={<IconChartPie size={22} stroke={1.5}/>}
            loading={loading}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
          <InfoCard
            title="Última Actualización"
            value={ultimaActualizacion ? ultimaActualizacion.label : "—"}
            subtitle={ultimaActualizacion ? ultimaActualizacion.date : " "}
            size="sm"
            icon={<IconCalendar size={22} stroke={1.5}/>}
            loading={loading}
          />
        </Grid.Col>
      </Grid>
      <EvidencesDistributionCard 
        loading={loading}
        pei={pei}
        evidenciasPorKpi={evidenciasPorKpi}
      />
      <Card radius="md" withBorder>
        <Text size="xl" fw={600}>
          Evidencias recientes
        </Text>
        <Text size="sm" c="dimmed">
          Las últimas evidencias cargadas en los ultimos 14 dias
        </Text>
        <Box mt="lg">
          <Table.ScrollContainer minWidth={500} maxHeight="60vh">
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th fw={500} w={'25%'}>Nombre</Table.Th>
                  <Table.Th fw={500} w={'15%'}>Unidad de Gestión</Table.Th>
                  <Table.Th fw={500} w={'40%'}>KPIs seleccionados</Table.Th>
                  <Table.Th fw={500} w={'10%'}>Usuario</Table.Th>
                  <Table.Th fw={500} w={'10%'}>Fecha</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {recentEvidences.length > 0 ? (
                  recentEvidences.map((ev) => (
                    <Table.Tr key={ev.Id}>
                      <Table.Td>
                        <Group gap={'xs'} wrap='nowrap'>
                          {ev.tieneValoracion && (
                            <ThemeIcon color='green' variant='light' radius="lg" title='Valorada'>
                              <IconFileCheck style={{ width: '70%', height: '70%' }} />
                            </ThemeIcon>
                          )}
                          {ev.titulo}
                        </Group>
                      </Table.Td>
                      <Table.Td>{ev.unidadesGestion[0]?.codigoUnidadGestion}-{ev.unidadesGestion[0]?.codigoSede}</Table.Td>
                      <Table.Td>
                        <Flex gap="xs" wrap="wrap">
                          {ev.kpis.map((kpi) => {
                            const k = allKpis.find((item) => item.Id === kpi.Id);
                            return (
                              <Badge key={kpi.Id} variant="dot" color='#e72166' title={k?.titulo} maw={250}>
                                <Text inherit truncate='end'>{k?.numeroCompleto}. {k?.titulo}</Text>
                              </Badge>
                            );
                          })}
                        </Flex>
                      </Table.Td>
                      <Table.Td>{ev.vc_insuser}</Table.Td>
                      <Table.Td>{dayjs(ev.dt_lu).format('DD/MM/YYYY')}</Table.Td>
                    </Table.Tr>
                  ))
                ) : (
                  <Table.Tr>
                    <Table.Td colSpan={4} ta="center">
                      No se han encontrado evidencias recientes.
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Box>
      </Card>
    </Stack>
  );
};

export default DashboardEvidences;
